import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenantBranding } from "../branding/TenantBrandingProvider";
import styles from "./analyticsConsent.module.css";
import {
  bootstrapGoogleAnalytics,
  getAnalyticsConsent,
  saveAnalyticsConsent,
  trackEvent,
  trackPageView,
  updateGoogleAnalyticsConsent,
} from "./googleAnalytics";

export default function AnalyticsConsentBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const { branding, isLoaded } = useTenantBranding();
  const [consent, setConsent] = useState(() => getAnalyticsConsent());
  const [isOpen, setIsOpen] = useState(() => getAnalyticsConsent() === null);

  const isPublicMarketingHome =
    isLoaded && location.pathname === "/" && Boolean(branding?.is_main);

  useEffect(() => {
    if (isPublicMarketingHome) {
      bootstrapGoogleAnalytics();
    }
  }, [isPublicMarketingHome]);

  useEffect(() => {
    if (!isPublicMarketingHome || consent !== "granted") return;

    trackPageView(`${location.pathname}${location.search}`);
  }, [consent, isPublicMarketingHome, location.pathname, location.search]);

  useEffect(() => {
    if (!isPublicMarketingHome || consent !== "granted") return undefined;

    const onClick = (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link) return;

      const href = link.getAttribute("href") || "";
      if (href.startsWith("#")) {
        trackEvent("landing_navigation_click", { link_target: href });
      } else if (href === "/login") {
        trackEvent("login_cta_click", { link_target: "/login" });
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [consent, isPublicMarketingHome]);

  const applyConsent = (nextConsent) => {
    const granted = nextConsent === "granted";

    saveAnalyticsConsent(nextConsent);
    updateGoogleAnalyticsConsent(granted);
    setConsent(nextConsent);
    setIsOpen(false);

    if (granted) {
      // The normal location effect handles the first hit after state update.
      window.__velsoftLastGaPageView = undefined;
    }
  };

  if (!isPublicMarketingHome) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <div className={styles.overlay} role="presentation">
          <section
            className={styles.banner}
            role="dialog"
            aria-modal="true"
            aria-labelledby="analytics-consent-title"
            aria-describedby="analytics-consent-text"
          >
            <div className={styles.copy}>
              <h2 id="analytics-consent-title" className={styles.title}>
                {t("seo_cookie_title")}
              </h2>
              <p id="analytics-consent-text" className={styles.text}>
                {t("seo_cookie_text")}
              </p>
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.button} ${styles.secondary}`}
                onClick={() => applyConsent("denied")}
              >
                {t("seo_cookie_necessary_only")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.primary}`}
                onClick={() => applyConsent("granted")}
              >
                {t("seo_cookie_allow_analytics")}
              </button>
            </div>
          </section>
        </div>
      )}

      {!isOpen && consent !== null && (
        <button
          type="button"
          className={styles.preferences}
          onClick={() => setIsOpen(true)}
          aria-label={t("seo_cookie_preferences")}
        >
          {t("seo_cookie_preferences")}
        </button>
      )}
    </>
  );
}
